import { getPlanChangesHelpArticleUrl } from '@/features/trialsAndUpgrades/trial-recap-modal/utils';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Evidence } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyP, TypographyP2 } from '@heidi/design-system/typography';
import { Blocks, Info, MessageSquareText, Wand2 } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { scribePlusRetainedFeatureListMessages } from '@heidi/commerce';

export type ScribePlusRetainedFeatureListVariant = 'scribe_plus' | 'scribe_team';

type ScribePlusRetainedFeatureListProps = {
  /** Scribe Team copy when the ended trial was Practice. */
  variant?: ScribePlusRetainedFeatureListVariant;
};

/**
 * Static list of capabilities the user / team keeps on Scribe Plus or
 * Scribe Team after the Clinician / Practice trial ends. Renders inside the
 * "Scribe Plus" / "Scribe Team" segmented-control tab of
 * {@link ClinicianTrialEndedModal}.
 *
 * Both variants share rows 1-3 (Limited in-session answers + Advanced
 * note templates + Unlimited AI note editing). The Scribe Team variant
 * additionally renders an Out-of-session Evidence row. The "Limited
 * in-session answers" row is gated on
 * `useEvidenceAccess().isInSessionEvidenceShown` so it stays hidden for
 * UK / EU EEA users who can't use in-session evidence at all — surfacing
 * it as a retained feature was misleading (release blocker). Those users
 * still see two rows (Advanced note templates + Unlimited AI note
 * editing) which is what Figma node 11262:37171 calls for.
 *
 * Figma:
 * - Scribe Plus: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10978-32435
 */
export function ScribePlusRetainedFeatureList({
  variant = 'scribe_plus',
}: ScribePlusRetainedFeatureListProps) {
  const intl = useIntl();
  const isScribeTeam = variant === 'scribe_team';
  const planChangesArticleUrl = getPlanChangesHelpArticleUrl(intl.locale);
  const sectionHeader = isScribeTeam
    ? scribePlusRetainedFeatureListMessages.sectionLabelScribeTeam
    : scribePlusRetainedFeatureListMessages.sectionLabelScribePlus;

  // Same gate as the upgrade-pitch (left) tab — keeps the in-session
  // evidence row hidden for UK / EU EEA cohorts.
  const { isInSessionEvidenceShown } = useEvidenceAccess();

  const rowClasses = cn(
    'flex items-center gap-2 px-2 py-4',
    'border-b border-border last:border-b-0'
  );

  return (
    <div className="flex w-full flex-col gap-2">
      <TypographyP2 className="font-medium text-text-secondary">
        <FormattedMessage
          {...sectionHeader}
          values={{
            link: (chunks) => (
              <Link
                variant="inline"
                tone="default"
                href={planChangesArticleUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {chunks}
              </Link>
            ),
          }}
        />
      </TypographyP2>
      <ul className="flex w-full flex-col">
        {isInSessionEvidenceShown && (
          <li
            className={rowClasses}
            data-testid="scribe-plus-retained-feature-limited-in-session-answers"
          >
            <MessageSquareText
              aria-hidden
              className="size-5 shrink-0 text-text-primary"
            />
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <TypographyP className="min-w-0 text-text-primary">
                <FormattedMessage
                  {...scribePlusRetainedFeatureListMessages.limitedInSessionAnswers}
                />
              </TypographyP>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={intl.formatMessage(
                      scribePlusRetainedFeatureListMessages.limitedInSessionAnswersTooltipAriaLabel
                    )}
                    className="size-6 shrink-0 text-text-secondary hover:text-text-primary"
                    data-testid="scribe-plus-retained-feature-limited-in-session-answers-info"
                  >
                    <Info aria-hidden />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-start">
                  <FormattedMessage
                    {...scribePlusRetainedFeatureListMessages.limitedInSessionAnswersTooltip}
                  />
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </div>
          </li>
        )}
        {/* Advanced note templates — always shown (Scribe Plus + Scribe
            Team both include the premium template library). Added per
            Figma node 11262:37171 so the UK / EU EEA cohort (where the
            "Limited in-session answers" row is hidden) still sees two
            retained-feature rows. */}
        <li
          className={rowClasses}
          data-testid="scribe-plus-retained-feature-advanced-note-templates"
        >
          <Blocks aria-hidden className="size-5 shrink-0 text-text-primary" />
          <TypographyP className="min-w-0 flex-1 text-text-primary">
            <FormattedMessage
              {...scribePlusRetainedFeatureListMessages.advancedNoteTemplates}
            />
          </TypographyP>
        </li>
        <li
          className={rowClasses}
          data-testid="scribe-plus-retained-feature-unlimited-ai-note-editing"
        >
          <Wand2 aria-hidden className="size-5 shrink-0 text-text-primary" />
          <TypographyP className="min-w-0 flex-1 text-text-primary">
            <FormattedMessage
              {...scribePlusRetainedFeatureListMessages.unlimitedAiNoteEditing}
            />
          </TypographyP>
        </li>
        {isScribeTeam && (
          <li
            className={rowClasses}
            data-testid="scribe-plus-retained-feature-out-of-session-evidence"
          >
            <Evidence aria-hidden size={20} className="shrink-0 text-text-primary" />
            <TypographyP className="min-w-0 flex-1 text-text-primary">
              <FormattedMessage
                {...scribePlusRetainedFeatureListMessages.outOfSessionEvidence}
              />
            </TypographyP>
          </li>
        )}
      </ul>
    </div>
  );
}
